import React, { Component } from 'react'
import { connect } from 'react-redux'
import { Redirect } from 'react-router-dom'
import PropTypes from 'prop-types'
import ReactTooltip from 'react-tooltip'
import { Helmet } from 'react-helmet'
import _ from 'lodash'

import { browserUtils } from '../../utils'
import { routeNames } from '../../routes'
import { GuidedSearchStepContent, LoadingIcon, AutocompleteGlossary } from '../../components'
import {
  setActiveStep,
  setDirtyStep,
  resetAllSteps,
  fetchSteps
} from '../../store/modules/guidedSearch.module'
import { setActiveFilterIds } from '../../store/modules/filters.module'
import './GuidedSearch.sass'


const isMobile = browserUtils.isMobileDevice()

class GuidedSearch extends Component {
  constructor() {
    super()
    this._advanceStep = this.advanceStep.bind(this)
    this.helmet = (
      <Helmet>
        <title>Guided Search | Gulf TREE</title>
      </Helmet>
    )
  }

  componentDidMount() {
    // Fetch steps data once on page load
    // Otherwise, reset step UI state and active filter IDs
    if (!this.props.steps.data) {
      this.props.fetchSteps()
    } else {
      this.props.resetAllSteps()
    }

    // Get full tool set
    this.props.setActiveFilterIds([])
  }

  advanceStep(e) {
    e.preventDefault()
    const idx = this.props.activeStepIdx

    this.props.setDirtyStep(idx)
    this.props.setActiveStep(idx + 1)
  }

  render() {
    const {
      activeStepIdx,
      steps,
      matches,
      setActiveFilterIds,
      resetAllSteps,
      glossary,
      isLoadingGlossary,
    } = this.props

    // Can't visit guided search on mobile, so redirect
    if (isMobile) {
      return <Redirect to={routeNames.filteredSearch} />
    }

    if (steps.isLoading || !steps.data) {
      return (
        <section className="center-vertical-wrapper">
          {this.helmet}
          <LoadingIcon />
        </section>
      )
    }

    const activeStep = steps.data[activeStepIdx]
    const isLastStep = activeStepIdx >= steps.data.length - 1

    return (
      <div className="guided-search-main">
        {this.helmet}

        <div>
          {/* Tooltip */}
          { activeStep.tooltip &&
            <React.Fragment>
              <div className="guided-search__autocomplete">
                <AutocompleteGlossary glossaryTerms={glossary} disabled={isLoadingGlossary} />
              </div>
              <div>
                <a className="tooltip-lg step-content-tooltip" data-tip data-for="content">?</a>
                <ReactTooltip
                  id="content"
                  place="bottom"
                  effect="solid"
                  class="tooltip-theme"
                >
                  <span>{activeStep.tooltip}</span>
                </ReactTooltip>
              </div>
            </React.Fragment>
          }
        </div>

        <section className="guided-search-container">

          {/* Step contents */}
          <GuidedSearchStepContent
            stepContent={activeStep}
            isLastStep={isLastStep}
            matches={matches}
            resetAllSteps={resetAllSteps}
            setActiveFilterIds={setActiveFilterIds}
          />

          {/* Buttons */}
          { !isLastStep &&
            <div className="guided-search-btns">
              <button
                type="button"
                className="btn-blue-transparent"
                onClick={this._advanceStep}
              >
                Skip
              </button>
              <button
                className="btn-blue-solid"
                onClick={this._advanceStep}
                disabled={!activeStep.hasAnsweredQuestions}
              >
                Submit
              </button>
            </div>
          }
        </section>
      </div>
    )
  }
}

GuidedSearch.propTypes = {
  activeStepIdx: PropTypes.number,
  steps: PropTypes.shape({
    data: PropTypes.array,
    isFetching: PropTypes.bool
  }),
  resetAllSteps: PropTypes.func,
  setActiveStep: PropTypes.func,
  setDirtyStep: PropTypes.func,
  setActiveFilterIds: PropTypes.func,
  matches: PropTypes.shape({
    matchesCount: PropTypes.number,
    totalCount: PropTypes.number
  }),
  glossary: PropTypes.array,
  isLoadingGlossary: PropTypes.bool,
};

const mapStateToProps = (state) => {
  const glossary = _.get(state.content, ['data', 'glossary', 'glossary_terms'])
  let glossaryTerms = []

  // Get flat list of all glossary terms to use in the glossary autocomplete component
  if (glossary) {
    glossaryTerms = _.flatMap(glossary, glossaryLetter => glossaryLetter.terms)
  }

  return {
    activeStepIdx: state.guidedSearch.activeStepIdx,
    steps: state.guidedSearch.steps,
    matches: state.filteredSearch.matches,
    glossary: glossaryTerms,
    isLoadingGlossary: state.content.isLoading,
  }
}

export default connect(
  mapStateToProps,
  {
    setActiveStep,
    setDirtyStep,
    resetAllSteps,
    fetchSteps,
    setActiveFilterIds
  }
)(GuidedSearch);
